import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addItemToCart, clearCart } from '../store/cartSlice';
import { toast as hotToast } from 'react-hot-toast';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../config';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import LoadingSpinner from '../Components/LoadingSpinner';
import { FiMinus, FiPlus, FiShoppingCart, FiChevronRight, FiZoomIn } from 'react-icons/fi';
import {
  HiOutlineClock,
  HiOutlineTag,
  HiOutlineShieldCheck,
  HiOutlineSparkles,
} from 'react-icons/hi';

/* ── helpers ── */
const IMG_PLACEHOLDER = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400"%3E%3Crect width="400" height="400" fill="%23f1f5f9"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="system-ui" font-size="14" fill="%2394a3b8"%3ENo Image%3C/text%3E%3C/svg%3E';

function StarRating({ value, count }) {
  const rounded = Math.round(value);
  return (
    <div className="flex items-center gap-2">
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((s) => (
          <span key={s} className={`text-lg leading-none ${s <= rounded ? 'text-yellow-400' : 'text-sand'}`}>★</span>
        ))}
      </div>
      <span className="text-sm font-semibold text-ink">{value.toFixed(1)}</span>
      {count > 0 && <span className="text-sm text-clay">({count} reviews)</span>}
    </div>
  );
}

function SpecRow({ label, value }) {
  if (!value && value !== false) return null;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-sand last:border-0">
      <span className="text-sm text-clay font-medium w-24 sm:w-32 shrink-0">{label}</span>
      <span className="text-sm text-ink font-medium flex-1">{value}</span>
    </div>
  );
}

/* ── Power formatting ── */
const fmtPower = (p) => {
  const n = parseFloat(p);
  if (n === 0) return 'Plano (0.00)';
  return n > 0 ? `+${n.toFixed(2)}` : n.toFixed(2);
};


/* ════════════════════════════════════════════════════════════ */
const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const dispatch = useDispatch();
  const { currentUser } = useSelector((state) => state.user);
  const prescription = useSelector((state) => state.prescription);
  const { isSet: prescriptionIsSet } = prescription;
  const prescriptionToastShownRef = useRef(false);

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [imgZoomed, setImgZoomed] = useState(false);
  const [leftEyePower, setLeftEyePower] = useState('');
  const [rightEyePower, setRightEyePower] = useState('');
  const [powerSelectorOpen, setPowerSelectorOpen] = useState(false);
  const [powerError, setPowerError] = useState(false);
  const powerRef = useRef(null);
  // Track which image indices are invalid (broken URLs or blank placeholders)
  const [imgErrors, setImgErrors] = useState(new Set());
  const markError = (idx) =>
    setImgErrors((prev) => new Set([...prev, idx]));

  // Reset error tracking when product changes
  useEffect(() => {
    if (product) setImgErrors(new Set());
  }, [product]);

  // Auto-open power selector when arriving from store card "Select" button
  useEffect(() => {
    const validPows = (product?.powersAvailable || []).filter(p => !isNaN(parseFloat(p)));
    if (product && locationState?.openPower && isLens(product) && validPows.length > 0) {
      setPowerSelectorOpen(true);
      setTimeout(() => {
        powerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [product, locationState]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setLoading(true);
    const fetchProduct = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/products/${id}`);
        setProduct(data.data);
        // Default to specifications for lenses, description for accessories
        const p = data.data;
        const hasSpecs = p.duration || p.diameter || p.baseCurve || p.waterContent || p.lensesMaterial;
        setActiveTab(hasSpecs ? 'specifications' : 'description');
      } catch {
        toast.error('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  // Auto-apply the user's saved prescription: pre-select the closest available
  // power for each eye when viewing a lens product.
  useEffect(() => {
    if (!product || !prescriptionIsSet) return;
    const pows = (product.powersAvailable || []).filter((p) => !isNaN(parseFloat(p)));
    if (!pows.length) return;
    const closest = (sph) => {
      if (sph == null || isNaN(parseFloat(sph))) return '';
      const target = parseFloat(sph);
      return pows.reduce(
        (best, p) => (Math.abs(parseFloat(p) - target) < Math.abs(parseFloat(best) - target) ? p : best),
        pows[0],
      );
    };
    const r = prescription.rightEye?.effectiveSph ?? prescription.rightEye?.sph;
    const l = prescription.leftEye?.effectiveSph ?? prescription.leftEye?.sph;
    setRightEyePower((cur) => cur || closest(r));
    setLeftEyePower((cur) => cur || closest(l));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, prescriptionIsSet]);

  const handleQuantityChange = (delta) => {
    setQuantity((q) => Math.max(1, q + delta));
  };

  const isLens = (p) =>
    p &&
    (p.category === 'Clear Lenses' ||
      p.category === 'Colored Lenses' ||
      p.category === 'Contact Lenses' ||
      (typeof p.category === 'string' &&
        p.category.length === 24 &&
        /^[0-9a-fA-F]{24}$/.test(p.category)));

  const handleAddToCart = () => {
    if (hasPowers && (leftEyePower === '' || rightEyePower === '')) {
      setPowerError(true);
      setPowerSelectorOpen(true);
      setTimeout(() => {
        powerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }
    setPowerError(false);
    dispatch(addItemToCart({
      product,
      quantity,
      leftEyePower:  hasPowers ? parseFloat(leftEyePower)  : 0,
      rightEyePower: hasPowers ? parseFloat(rightEyePower) : 0,
    }));
    toast.success('Item added to your cart.');

    // Gentle nudge: show once per session if user hasn't set prescription yet
    if (hasPowers && !prescriptionIsSet && !prescriptionToastShownRef.current) {
      prescriptionToastShownRef.current = true;
      setTimeout(() => {
        hotToast(
          (t) => (
            <div className="flex flex-col gap-1">
              <p className="font-semibold text-sm">Don't know your prescription?</p>
              <p className="text-xs text-gray-500">Find lenses matched to your eye power.</p>
              <a
                href="/eye-power-finder"
                onClick={() => hotToast.dismiss(t.id)}
                className="mt-1 text-xs font-semibold text-black underline underline-offset-2 self-start"
              >
                Try Eye Power Finder →
              </a>
            </div>
          ),
          { duration: 6000, icon: '👁️' }
        );
      }, 1500);
    }
  };

  const handleBuyNow = () => {
    dispatch(clearCart());
    dispatch(addItemToCart({
      product,
      quantity,
      leftEyePower:  isLens(product) ? parseFloat(leftEyePower)  : 0,
      rightEyePower: isLens(product) ? parseFloat(rightEyePower) : 0,
    }));
    navigate('/cart');
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex flex-col">
        <Navbar />
        <div className="flex-grow flex items-center justify-center">
          <LoadingSpinner fullScreen={false} />
        </div>
        <Footer />
      </div>
    );
  }

  /* ── Not found ── */
  if (!product) {
    return (
      <div className="min-h-screen bg-cream flex flex-col">
        <Navbar />
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <p className="font-display font-normal tracking-tight text-2xl text-ink mb-2">Product not found</p>
            <button onClick={() => navigate(-1)} className="text-brand-600 hover:text-brand-700 hover:underline text-sm">
              ← Go back
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  /* ── Derived data ── */
  // All non-empty URLs — broken ones are tracked via imgErrors at runtime
  const rawImages = (product.images || []).filter((img) => img && img.trim() !== '');
  if (rawImages.length === 0) rawImages.push(IMG_PLACEHOLDER);
  // Only images that actually loaded (not errored)
  const visibleIndices = rawImages
    .map((_, idx) => idx)
    .filter((idx) => !imgErrors.has(idx));
  const validImages = visibleIndices.map((idx) => rawImages[idx]);
  if (validImages.length === 0) validImages.push(IMG_PLACEHOLDER);
  // Keep selectedImage clamped to visible range
  const safeSelected = Math.min(selectedImage, visibleIndices.length - 1);
  const safeRawIdx   = visibleIndices[safeSelected] ?? 0;

  const displayPrice    = product.hasDiscount ? product.priceAfterDiscount : product.price;
  const originalPrice   = product.hasDiscount ? product.price : null;
  const discountPercent = product.hasDiscount
    ? Math.round(((product.price - product.priceAfterDiscount) / product.price) * 100)
    : 0;

  const lensPowers = (product.powersAvailable || []).filter(p => !isNaN(parseFloat(p)));
  const hasPowers = isLens(product) && lensPowers.length > 0;

  const categoryLabel =
    typeof product.category === 'string' &&
    product.category.length === 24 &&
    /^[0-9a-fA-F]{24}$/.test(product.category)
      ? 'Contact Lenses'
      : product.category;

  /* ════════════════════════ RENDER ════════════════════════ */
  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <Navbar />

      <main className="flex-grow container mx-auto px-4 py-8 max-w-6xl">

        {/* ── Breadcrumb ── */}
        <nav className="flex items-center gap-1.5 text-sm text-clay mb-8">
          <Link to="/" className="hover:text-brand-600 transition-colors">Home</Link>
          <FiChevronRight className="w-3.5 h-3.5" />
          {categoryLabel && (
            <>
              <Link to={`/products/${typeof product.category === 'string' && product.category.length !== 24 ? product.category.toLowerCase().replace(/\s+/g, '-') : 'colored-lenses'}`} className="hover:text-brand-600 transition-colors">{categoryLabel}</Link>
              <FiChevronRight className="w-3.5 h-3.5" />
            </>
          )}
          <span className="text-ink font-medium truncate max-w-[140px] sm:max-w-xs">{product.name}</span>
        </nav>

        {/* ── Main Grid ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

          {/* ══ Left — Image Gallery ══ */}
          <div className="space-y-3">

            {/* Main image */}
            <div
              className="relative aspect-square rounded-2xl overflow-hidden bg-white border border-sand shadow-card cursor-zoom-in"
              onMouseEnter={() => setImgZoomed(true)}
              onMouseLeave={() => setImgZoomed(false)}
            >
              <img
                key={safeRawIdx}
                src={rawImages[safeRawIdx]}
                alt={product.name}
                onError={() => {
                  markError(safeRawIdx);
                  // Move to first still-valid index
                  const next = visibleIndices.find((i) => i !== safeRawIdx);
                  if (next !== undefined) setSelectedImage(visibleIndices.indexOf(next));
                }}
                className={`w-full h-full object-cover transition-transform duration-500 ${
                  imgZoomed ? 'scale-110' : 'scale-100'
                }`}
              />

              {/* Discount badge over image */}
              {product.hasDiscount && (
                <div className="absolute top-4 left-4">
                  <span className="bg-red-500 text-white text-sm font-bold py-1.5 px-3 rounded-full shadow-sm">
                    -{discountPercent}%
                  </span>
                </div>
              )}

              {/* Zoom hint */}
              <div className="absolute bottom-4 right-4 bg-white/80 backdrop-blur-sm rounded-full p-2 shadow-sm opacity-60">
                <FiZoomIn className="w-4 h-4 text-clay" />
              </div>

              {/* Image counter — based on actually loaded images */}
              {validImages.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/40 backdrop-blur-sm text-white text-xs font-medium px-3 py-1 rounded-full">
                  {safeSelected + 1} / {validImages.length}
                </div>
              )}
            </div>

            {/* Thumbnails — only non-empty URLs; hidden as they error out */}
            {rawImages.length > 1 && (
              <div className="flex gap-2 flex-wrap">
                {rawImages.map((img, rawIdx) => {
                  // Skip if this URL already errored
                  if (imgErrors.has(rawIdx)) return null;
                  const visiblePos = visibleIndices.indexOf(rawIdx);
                  return (
                    <button
                      key={rawIdx}
                      onClick={() => setSelectedImage(visiblePos)}
                      className={`w-16 h-16 rounded-xl overflow-hidden border-2 transition-all duration-200 flex-shrink-0 ${
                        safeRawIdx === rawIdx
                          ? 'border-brand-500 shadow-md'
                          : 'border-sand hover:border-brand-300'
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${product.name} ${rawIdx + 1}`}
                        onError={() => markError(rawIdx)}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ══ Right — Product Info ══ */}
          <div className="flex flex-col gap-5">

            {/* Name + rating */}
            <div>
              <h1 className="font-display font-normal tracking-tight text-2xl md:text-3xl text-ink leading-tight mb-2">
                {product.name}
              </h1>
              {product.ratingsAverage > 0 && (
                <StarRating value={product.ratingsAverage} count={product.ratingsQuantity} />
              )}
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-3">
              <span className="font-display font-normal text-3xl text-ink tracking-tight">
                EGP {Number(displayPrice).toLocaleString()}
              </span>
              {originalPrice && (
                <span className="text-lg text-clay line-through">
                  EGP {Number(originalPrice).toLocaleString()}
                </span>
              )}
              {product.hasDiscount && (
                <span className="text-sm font-bold bg-red-50 text-red-500 border border-red-100 px-2.5 py-1 rounded-full">
                  {discountPercent}% OFF
                </span>
              )}
            </div>

            {/* Badges */}
            <div className="flex flex-wrap gap-2">
              {categoryLabel && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold bg-cream text-brand-700 border border-brand-300 px-3 py-1 rounded-full">
                  <HiOutlineTag className="w-3.5 h-3.5" />
                  {categoryLabel}
                </span>
              )}
              {product.subCategory?.name && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold bg-cream text-brand-700 border border-brand-300 px-3 py-1 rounded-full">
                  {product.subCategory.name}
                </span>
              )}
              {product.duration && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100 px-3 py-1 rounded-full">
                  <HiOutlineClock className="w-3.5 h-3.5" />
                  {product.duration}
                </span>
              )}
              {product.uvProtection && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100 px-3 py-1 rounded-full">
                  <HiOutlineShieldCheck className="w-3.5 h-3.5" />
                  UV Protection
                </span>
              )}
            </div>

            {/* ── Prescription Suggestion (guide only) ── */}
            {hasPowers && prescriptionIsSet && (() => {
              const fmt = (n) => n === null || n === undefined ? null : n === 0 ? 'Plano' : (n > 0 ? `+${Math.abs(n).toFixed(2)}` : `-${Math.abs(n).toFixed(2)}`);
              const rSph = fmt(prescription.rightEye?.effectiveSph ?? prescription.rightEye?.sph);
              const lSph = fmt(prescription.leftEye?.effectiveSph  ?? prescription.leftEye?.sph);
              if (!rSph && !lSph) return null;
              return (
                <div className="rounded-xl bg-cream border border-brand-300 px-4 py-3 flex items-start gap-3">
                  <span className="text-brand-500 mt-0.5 shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><circle cx="11" cy="11" r="3"/>
                    </svg>
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-brand-700 mb-1">Your saved prescription (guide only)</p>
                    <div className="flex flex-wrap gap-2">
                      {rSph && (
                        <span className="text-xs bg-white border border-brand-300 text-brand-700 px-2.5 py-1 rounded-full font-mono font-semibold">
                          OD {rSph}
                        </span>
                      )}
                      {lSph && (
                        <span className="text-xs bg-white border border-brand-300 text-brand-700 px-2.5 py-1 rounded-full font-mono font-semibold">
                          OS {lSph}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-clay mt-1.5">
                      This is your estimated contact lens power. Use it as a reference when selecting below.
                    </p>
                  </div>
                  <a href="/eye-power-finder" className="text-xs text-brand-600 hover:text-brand-700 underline underline-offset-2 shrink-0 mt-0.5">Edit</a>
                </div>
              );
            })()}

            {/* ── Lens Power Selector ── */}
            {hasPowers && (
              <div
                ref={powerRef}
                className={`rounded-2xl border p-5 transition-colors duration-200 ${powerError ? 'border-red-400 bg-red-50/60' : 'border-brand-300 bg-cream'}`}
              >
                <button
                  onClick={() => { setPowerSelectorOpen((o) => !o); setPowerError(false); }}
                  className="w-full flex items-center justify-between gap-2"
                >
                  <div className="flex items-center gap-2">
                    <HiOutlineSparkles className="w-4 h-4 text-brand-600" />
                    <h3 className="text-sm font-bold text-ink uppercase tracking-wide">
                      Select Power for Both Eyes
                    </h3>
                  </div>
                  <span className={`transition-transform duration-200 ${powerSelectorOpen ? 'rotate-180' : ''} ${powerError ? 'text-red-500' : 'text-brand-600'}`}>
                    ▾
                  </span>
                </button>

                {powerError && (
                  <p className="text-red-500 text-xs font-semibold mt-2">
                    Please select your lens power before adding to cart.
                  </p>
                )}

                {powerSelectorOpen && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    {/* Right Eye */}
                    <div>
                      <label className="block text-xs font-semibold text-brand-700 mb-1.5 uppercase tracking-wider">
                        Right Eye (OD)
                      </label>
                      <select
                        value={rightEyePower}
                        onChange={(e) => { setRightEyePower(e.target.value); setPowerError(false); }}
                        className="w-full rounded-xl border border-sand bg-white py-2.5 px-3 text-sm text-ink font-medium focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-shadow"
                      >
                        <option value="" disabled>-- Select Power --</option>
                        {lensPowers.map((p) => (
                          <option key={`right-${p}`} value={p}>{fmtPower(p)}</option>
                        ))}
                      </select>
                    </div>

                    {/* Left Eye */}
                    <div>
                      <label className="block text-xs font-semibold text-brand-700 mb-1.5 uppercase tracking-wider">
                        Left Eye (OS)
                      </label>
                      <select
                        value={leftEyePower}
                        onChange={(e) => { setLeftEyePower(e.target.value); setPowerError(false); }}
                        className="w-full rounded-xl border border-sand bg-white py-2.5 px-3 text-sm text-ink font-medium focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-shadow"
                      >
                        <option value="" disabled>-- Select Power --</option>
                        {lensPowers.map((p) => (
                          <option key={`left-${p}`} value={p}>{fmtPower(p)}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Quantity ── */}
            <div>
              <label className="block text-xs font-semibold text-clay mb-2 uppercase tracking-wider">
                Quantity
              </label>
              <div className="inline-flex items-center rounded-xl border border-sand bg-white overflow-hidden shadow-sm">
                <button
                  onClick={() => handleQuantityChange(-1)}
                  disabled={quantity <= 1}
                  className="w-11 h-11 flex items-center justify-center text-clay hover:bg-cream disabled:opacity-30 transition-colors"
                >
                  <FiMinus className="w-4 h-4" />
                </button>
                <div className="w-12 h-11 flex items-center justify-center font-bold text-ink text-base border-x border-sand">
                  {quantity}
                </div>
                <button
                  onClick={() => handleQuantityChange(1)}
                  className="w-11 h-11 flex items-center justify-center text-clay hover:bg-cream transition-colors"
                >
                  <FiPlus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* ── CTA Buttons ── */}
            <div className="flex gap-3 pt-1">
              <button
                onClick={handleAddToCart}
                className="flex-1 h-13 py-3.5 rounded-xl bg-ink text-cream hover:bg-brand-500 hover:text-ink font-bold text-sm tracking-wide transition-colors duration-200 hover:shadow-lg active:scale-[0.98] flex items-center justify-center gap-2"
              >
                <FiShoppingCart className="w-4 h-4" />
                Add to Cart
              </button>
            </div>

            {/* Quick specs strip */}
            {(product.lensesMaterial || product.diameter || product.package) && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
                {product.lensesMaterial && (
                  <div className="rounded-xl bg-white border border-sand p-3 text-center">
                    <p className="text-xs text-clay font-medium mb-0.5">Material</p>
                    <p className="text-xs font-bold text-ink">{product.lensesMaterial}</p>
                  </div>
                )}
                {product.diameter && (
                  <div className="rounded-xl bg-white border border-sand p-3 text-center">
                    <p className="text-xs text-clay font-medium mb-0.5">Diameter</p>
                    <p className="text-xs font-bold text-ink">{product.diameter}</p>
                  </div>
                )}
                {product.package && (
                  <div className="rounded-xl bg-white border border-sand p-3 text-center">
                    <p className="text-xs text-clay font-medium mb-0.5">Package</p>
                    <p className="text-xs font-bold text-ink">{product.package}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Tabs Section ── */}
        <div className="mt-12 bg-white rounded-2xl border border-sand shadow-card overflow-hidden">

          {/* Tab bar */}
          <div className="flex border-b border-sand">
            {['description', 'specifications'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 sm:px-8 py-3 sm:py-4 text-xs sm:text-sm font-semibold capitalize transition-all duration-200 border-b-2 -mb-px ${
                  activeTab === tab
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-clay hover:text-ink'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="p-6 md:p-8">

            {/* ── Description Tab ── */}
            {activeTab === 'description' && (
              <div className="animate-fade-in">
                {product.description ? (
                  <p className="text-clay leading-relaxed text-[0.9375rem]">
                    {product.description}
                  </p>
                ) : (
                  <p className="text-clay italic">No description available.</p>
                )}
              </div>
            )}

            {/* ── Specifications Tab ── */}
            {activeTab === 'specifications' && (
              <div className="animate-fade-in space-y-6">

                {/* Core specs grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 divide-y sm:divide-y-0">
                  <div className="sm:border-r sm:border-sand sm:pr-8 space-y-0">
                    <SpecRow label="Duration"      value={product.duration} />
                    <SpecRow label="Package"       value={product.package} />
                    <SpecRow label="Diameter"      value={product.diameter} />
                    <SpecRow label="Base Curve"    value={product.baseCurve} />
                  </div>
                  <div className="sm:pl-8 space-y-0">
                    <SpecRow label="Water Content" value={product.waterContent} />
                    <SpecRow label="Material"      value={product.lensesMaterial} />
                    <SpecRow label="UV Protection" value={product.uvProtection !== undefined && product.uvProtection !== null ? (product.uvProtection ? 'Yes' : 'No') : undefined} />
                    <SpecRow label="SKU"           value={product.sku} />
                  </div>
                </div>

                {/* Available Powers */}
                {product.powersAvailable && product.powersAvailable.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-clay mb-3">
                      Available Powers
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {product.powersAvailable.map((p) => (
                        <span key={p} className="px-2.5 py-1 bg-cream border border-sand rounded-lg text-xs font-medium text-ink">
                          {fmtPower(p)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Available Colors */}
                {product.colorsAvailable && product.colorsAvailable.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-clay mb-3">
                      Available Colors
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {product.colorsAvailable.map((c) => (
                        <span key={c} className="px-3 py-1 bg-cream border border-brand-300 text-brand-700 rounded-full text-sm font-medium">
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Features */}
                {product.features && product.features.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-clay mb-3">
                      Features
                    </p>
                    <ul className="space-y-2">
                      {product.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-ink">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-500 flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Tags */}
                {product.tags && product.tags.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-clay mb-3">
                      Tags
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {product.tags.map((t) => (
                        <span key={t} className="px-3 py-1 bg-cream text-clay border border-sand rounded-full text-xs font-medium">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Additional Notes */}
                {product.additionalNotes && (
                  <div className="p-4 bg-cream rounded-xl border border-sand text-sm text-clay">
                    {product.additionalNotes}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetails;
